import { Controller } from 'react-hook-form'
import React from 'react'
import { CircleHelp, Star } from 'lucide-react'
import { Label } from '@radix-ui/react-label'

import Container from 'src/components/Container'
import AppBreadcrumb from 'src/components/AppBreadcrumb'

import { FieldType } from 'src/pages/ServiceContacts/types'
import {
  CIRCLE_HELP_SIZE,
  STAR_SIZE,
} from 'src/pages/ServiceContacts/constants'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/shadcn/tooltip'
import { Textarea } from 'src/components/shadcn/textarea'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
} from 'src/components/shadcn/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/shadcn/popover'
import { Input } from 'src/components/shadcn/input'
import { Card, CardContent, CardHeader } from 'src/components/shadcn/card'
import { Button } from 'src/components/shadcn/button'
import { useServiceContacts } from './useServiceContacts'

const ServiceContacts = (): JSX.Element => {
  const {
    locale,
    breadcrumbItems,
    listOfFields,
    handleSubmit,
    onSubmit,
    control,
    errors,
    isFavorite,
    handleFavoriteToggle,
    isMobile,
  } = useServiceContacts()

  return (
    <Container>
      <AppBreadcrumb items={breadcrumbItems} />
      <div className="flex justify-center w-full">
        <Card className="w-full md:w-2/4 mt-8 rounded-xl">
          <CardHeader className="p-6 border-b mb-2">
            <h1 className="text-2xl font-bold">{locale.pageTitle}</h1>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {listOfFields.map(
                ({ id, label, placeholder, type, validation, options }) => (
                  <div key={id}>
                    <h1 className="text-lg font-medium mb-2">{label}</h1>
                    {(() => {
                      switch (type) {
                        case FieldType.INPUT:
                          return (
                            <Input
                              id={id}
                              placeholder={placeholder}
                              {...control.register(id, validation)}
                            />
                          )

                        case FieldType.TEXTAREA:
                          return (
                            <Textarea
                              id={id}
                              placeholder={placeholder}
                              {...control.register(id, validation)}
                            />
                          )

                        case FieldType.SELECT:
                          return (
                            <Controller
                              name={id}
                              control={control}
                              rules={validation}
                              render={({ field }) => (
                                <Select
                                  value={String(field.value)}
                                  onValueChange={field.onChange}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={placeholder} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectGroup>
                                      <SelectLabel>{label}</SelectLabel>
                                      {options?.map(({ value, label }) => (
                                        <SelectItem key={value} value={value}>
                                          {label}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              )}
                            />
                          )

                        default:
                          return null
                      }
                    })()}
                    {errors[id] && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors[id]?.message}
                      </p>
                    )}
                  </div>
                ),
              )}

              <div className="flex justify-between">
                <div className="flex gap-2">
                  <Label className="text-base font-bold">
                    {locale.favorite}
                  </Label>
                  {isMobile ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <span className="max-w-[150px] cursor-pointer mt-1">
                          <CircleHelp size={CIRCLE_HELP_SIZE} />
                        </span>
                      </PopoverTrigger>
                      <PopoverContent className="whitespace-normal break-words max-w-xs">
                        {locale.favoriteInfo}
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="max-w-[150px] cursor-pointer mt-1">
                          <CircleHelp size={CIRCLE_HELP_SIZE} />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        align="end"
                        className="whitespace-normal break-words max-w-xs"
                      >
                        {locale.favoriteInfo}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <button
                  type="button"
                  className={`text-gray-500 ${isFavorite ? 'text-yellow-300' : 'hover:text-yellow-300'}`}
                  onClick={handleFavoriteToggle}
                >
                  <Star
                    size={STAR_SIZE}
                    fill={isFavorite ? 'currentColor' : 'transparent'}
                  />
                </button>
              </div>

              <div className="flex items-center justify-center">
                <Button className="w-[100px] rounded-[10px]" type="submit">
                  {locale.submitButton}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Container>
  )
}

export default ServiceContacts
